import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import SpecialtiesError from "../../../components/SpecialtiesError";
import {
  Flex,
  Toc,
  Type,
  Icon,
  Button,
  Toast,
} from "discover.medical.react-component-library";
import { useStore, useDispatch } from "../../../store";
import {
  fetchSpecialtyDetails,
  unFollowSpecialty,
  followSpecialty,
} from "../../../store/specialties/actions";
import { MainSpinner, FollowDialog } from "../../../components";
import DetailsListItem from "./DetailsListItem";
import { logBrowseItemFollowedCanceled } from "../../../util/analytics";
import {
  useFollowDialogStore,
  SubscribeToNothing,
} from "../../../components/FollowDialog/FollowDialogContext";
import { EVENT_BUS_EVENTS, useEventBus } from "../../../util/useEventBus";
import {
  CTA_EVENT_TYPE,
  CTA_TRIGGER_ACTION,
  useLaunchCTA,
} from "../../../hooks/useLaunchCTA";
const affordance = "specialtyDetails-list";

const formatSpecialtyId = (specialtyId) =>
  specialtyId.toLowerCase().replace(/[\s/]/g, "_");

const SpecialtyDetailsLayout = () => {
  const { specialtyId } = useParams();
  const dispatch = useDispatch();
  const store = useStore();
  const isOnSpecialtyDetails = true;
  const [toast, setToast] = useState(false);
  const [, setFollowDialogStore] = useFollowDialogStore(SubscribeToNothing);

  const {
    isFetching,
    specialtyDetails,
    specialtyFollowError,
    hasError,
  } = store.specialties;

  const path = useLocation().pathname;

  const eventBus = useEventBus();
  useEffect(() => {
    const handler = ({ specialtyId: unfollowedSpecialtyId }) => {
      if (unfollowedSpecialtyId !== formatSpecialtyId(specialtyId)) {
        return;
      }
      fetchSpecialtyDetails(dispatch, specialtyId);
    };
    eventBus.on(EVENT_BUS_EVENTS.SPECIALTY_UNSUBSCRIBED, handler);

    return () =>
      eventBus.detach(EVENT_BUS_EVENTS.SPECIALTY_UNSUBSCRIBED, handler);
  }, [eventBus, dispatch, specialtyId]);

  useEffect(() => {
    fetchSpecialtyDetails(dispatch, specialtyId, path);
  }, [specialtyId, dispatch, path]);

  useEffect(() => {
    if (specialtyFollowError?.followError?.message) {
      setToast(true);
    }
  }, [specialtyFollowError]);

  const handleToasterClose = () => {
    setToast(false);
  };

  const { launchCTA } = useLaunchCTA({ eventBus });
  const handleFollowSpecialty = () => {
    if (specialtyDetails?.modalData) {
      setFollowDialogStore({
        isOpen: true,
        saveData: specialtyDetails.followData,
      });
    } else if (specialtyDetails?.followData?.functionArgs?.subscriptionType) {
      followSpecialty(
        dispatch,
        specialtyDetails?.followData?.functionArgs?.apiPath,
        specialtyDetails?.followData?.functionArgs?.specialtyId,
        affordance,
        specialtyDetails?.followData?.functionArgs?.feedId,
        specialtyDetails?.followData?.functionArgs?.subscriptionType,
        isOnSpecialtyDetails
      );
    } else {
      launchCTA({
        userAction: CTA_TRIGGER_ACTION.FOLLOW_ICON_CLICK,
        type: CTA_EVENT_TYPE.ARTICLE_SUBSCRIPTION_BROWSE,
        component: "SpecialtyDetailsLayout",
      });
    }
  };

  const handleFollowIconClick = (feedId) => {
    if (!specialtyDetails?.followData?.functionArgs?.isFollowing) {
      handleFollowSpecialty();
    } else {
      unFollowSpecialty(
        dispatch,
        specialtyId,
        affordance,
        feedId,
        isOnSpecialtyDetails
      );
    }
  };

  const handleFollowActions = (followData, subscriptionType) => {
    followSpecialty(
      dispatch,
      followData?.functionArgs?.apiPath,
      followData?.functionArgs?.specialtyId,
      affordance,
      followData?.functionArgs?.feedId,
      subscriptionType,
      isOnSpecialtyDetails
    );
    setFollowDialogStore({ isOpen: false });
  };

  const handleFollowDialogCancel = () => {
    setFollowDialogStore({ isOpen: false, saveData: null });
    logBrowseItemFollowedCanceled(affordance, specialtyId);
  };

  if (isFetching) {
    return <MainSpinner />;
  }
  if (hasError || specialtyDetails?.error) {
    return <SpecialtiesError {...specialtyDetails?.error} />;
  }
  return (
    <>
      <Flex
        container
        justify={{ content: "space-between" }}
        align={{ item: "center" }}
      >
        <Flex cell column>
          <Type
            data-testid="specialty-detail-header-label"
            element="p"
            fontSize="md"
            marginBottom="sm"
          >
            {specialtyDetails?.header?.label}
          </Type>
          <Type
            element="h1"
            fontFamily="secondary"
            color="gray800"
            paddingBottom="sm"
            data-testid="specialty-detail-header-description"
          >
            {specialtyDetails?.header?.description}
          </Type>
        </Flex>
        {specialtyDetails?.followData &&
          Object.keys(specialtyDetails?.followData).length > 0 && (
            <Button
              kind="text"
              variant="light"
              data-testid="follow-button"
              onClick={() =>
                handleFollowIconClick(
                  specialtyDetails?.followData?.functionArgs?.feedId
                )
              }
            >
              <Icon
                icon={{
                  glyph: specialtyDetails?.followData?.icon?.glyph,
                  size: specialtyDetails?.followData?.icon?.size,
                  color: specialtyDetails?.followData?.icon?.color,
                }}
              />
              <span>{specialtyDetails?.followData?.label}</span>
            </Button>
          )}
      </Flex>
      <Toc.List>
        {specialtyDetails?.specialties?.map((specialty) => (
          <DetailsListItem
            key={specialty.label}
            detailsToRender={specialty}
            telemetryData={specialty.telemetryData}
            sharedTelemetryData={specialtyDetails.sharedTelemetryData}
          />
        ))}
      </Toc.List>
      {specialtyDetails?.modalData && (
        <FollowDialog
          modalData={specialtyDetails.modalData}
          onRequestClose={handleFollowDialogCancel}
          onSave={handleFollowActions}
        />
      )}
      {specialtyFollowError?.followError?.message && (
        <Toast
          isOpen={toast}
          onClose={handleToasterClose}
          data-testid="follow-toast"
          type="error"
          children={specialtyFollowError?.followError?.message}
        />
      )}
    </>
  );
};

SpecialtyDetailsLayout.displayName = "SpecialtyDetailsLayout";

export default SpecialtyDetailsLayout;
